import "./puzzle-fighter.css";

export type PuzzleColor = "red" | "blue" | "green" | "yellow";
export type PuzzleGem = { color: PuzzleColor | null; kind: "normal" | "crash" | "garbage"; countdown?: number };
export type PuzzlePair = { first: PuzzleGem; second: PuzzleGem };
export type PuzzlePlayerState = {
  board: Array<PuzzleGem | null>;
  pieceIndex: number;
  pendingGarbage: number;
  score: number;
  cleared: number;
  maxChain: number;
  lastChain: number;
  lastAttack: number;
  toppedOut: boolean;
  readyAt: number;
  currentPair: PuzzlePair | null;
  nextPair: PuzzlePair | null;
};
export type PuzzleFighterView = {
  playerOrder: string[];
  players: Record<string, PuzzlePlayerState | null>;
  winnerId: string | null;
  loserId: string | null;
  moveCount: number;
  lastMove: { playerId: string; column: number; orientation: 0 | 1 | 2 | 3; cleared: number; chain: number; attack: number } | null;
  canAct: boolean;
  canActInMs: number | null;
  waitingForOpponent?: boolean;
  autoDropInMsByPlayer?: Record<string, number | null>;
  fallDurationMs?: number;
  fallDurationMsByPlayer?: Record<string, number | null>;
  activeDropsByPlayer?: Record<string, { column: number; orientation: 0 | 1 | 2 | 3; expectedPieceIndex: number } | null>;
  legalDrops: Array<{ column: number; orientation: 0 | 1 | 2 | 3 }>;
};
export type PuzzleFighterPlayer = { id: string; name: string; score: number; connected: boolean; isAdmin: boolean };

const escapeHtml = (value: string) => value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;" })[character]!);
const nameFor = (players: PuzzleFighterPlayer[], id: string) => players.find((player) => player.id === id)?.name || "Fighter";

function gemHtml(gem: PuzzleGem | null, extraClass = "") {
  if (!gem) return `<i class="pf-gem is-empty ${extraClass}" aria-hidden="true"></i>`;
  const color = gem.color || "garbage";
  const art = gem.kind === "normal" ? `<svg class="pf-crystal" viewBox="0 0 100 100" aria-hidden="true"><path fill="var(--gem-deep)" d="M15 2H85L98 15V85L85 98H15L2 85V15Z"/><path fill="var(--gem-bright)" d="M15 2H85L75 22H25L2 15Z"/><path fill="var(--gem-core)" d="M2 15L25 22V76L15 98L2 85Z"/><path fill="var(--gem-mid)" d="M85 2L98 15V85L76 76V23Z"/><path fill="var(--gem-deep)" d="M15 98L25 76H76L98 85L85 98Z"/><path fill="var(--gem-core)" d="M25 22H75L79 72L72 79H28L21 72Z"/><path fill="white" opacity=".24" d="M25 22H75L21 72Z"/><path fill="white" opacity=".12" d="M75 22L79 72L72 79H28Z"/><path fill="none" stroke="white" stroke-opacity=".7" stroke-width="1.5" d="M15 3H85L97 15M3 15L24 22H75L85 3M24 22L22 73"/><path fill="none" stroke="var(--gem-bright)" stroke-width="2" d="M27 79H73L78 73"/><path fill="white" d="M18 9L20 16L27 18L20 20L18 27L16 20L9 18L16 16Z"/></svg>` : gem.kind === "crash" ? "✦" : gem.countdown || "";
  return `<i class="pf-gem pf-gem--${color} pf-gem--${gem.kind} ${extraClass}" aria-label="${gem.kind === "garbage" ? `Counter gem, ${gem.countdown || 0} turns` : `${color} ${gem.kind} gem`}">${art}</i>`;
}

function visualPowerGemCells(board: Array<PuzzleGem | null>) {
  const cells = new Set<number>();
  for (let row = 0; row < PUZZLE_ROWS - 1; row += 1) {
    for (let column = 0; column < PUZZLE_COLUMNS - 1; column += 1) {
      const square = [row * PUZZLE_COLUMNS + column, row * PUZZLE_COLUMNS + column + 1, (row + 1) * PUZZLE_COLUMNS + column, (row + 1) * PUZZLE_COLUMNS + column + 1];
      const gems = square.map((index) => board[index]);
      if (gems.every((gem) => gem?.kind === "normal" && gem.color === gems[0]?.color)) square.forEach((index) => cells.add(index));
    }
  }
  return cells;
}

function visualPowerGemClasses(board: Array<PuzzleGem | null>, powerCells: Set<number>, index: number) {
  if (!powerCells.has(index)) return "";
  const gem = board[index];
  const row = Math.floor(index / PUZZLE_COLUMNS);
  const column = index % PUZZLE_COLUMNS;
  const joins = (candidate: number, candidateRow: number) => powerCells.has(candidate) && Math.floor(candidate / PUZZLE_COLUMNS) === candidateRow && board[candidate]?.color === gem?.color;
  return [
    "is-power",
    column > 0 && joins(index - 1, row) ? "joins-left" : "",
    column < PUZZLE_COLUMNS - 1 && joins(index + 1, row) ? "joins-right" : "",
    row > 0 && joins(index - PUZZLE_COLUMNS, row - 1) ? "joins-up" : "",
    row < PUZZLE_ROWS - 1 && joins(index + PUZZLE_COLUMNS, row + 1) ? "joins-down" : "",
  ].filter(Boolean).join(" ");
}

function pairHtml(pair: PuzzlePair | null, label: string) {
  return `<div class="pf-pair"><small>${label}</small><div>${pair ? `${gemHtml(pair.first)}${gemHtml(pair.second)}` : "—"}</div></div>`;
}

const PUZZLE_COLUMNS = 6;
const PUZZLE_ROWS = 12;

function lowestOpenPuzzleRow(board: Array<PuzzleGem | null>, column: number) {
  for (let row = PUZZLE_ROWS - 1; row >= 0; row -= 1) if (!board[row * PUZZLE_COLUMNS + column]) return row;
  return -1;
}

export function puzzleFighterLandingRow(board: Array<PuzzleGem | null>, column: number, orientation: 0 | 1 | 2 | 3) {
  if (orientation === 1 || orientation === 3) {
    const secondColumn = column + (orientation === 1 ? 1 : -1);
    if (column < 0 || column >= PUZZLE_COLUMNS || secondColumn < 0 || secondColumn >= PUZZLE_COLUMNS) return -1;
    return Math.min(lowestOpenPuzzleRow(board, column), lowestOpenPuzzleRow(board, secondColumn));
  }
  return lowestOpenPuzzleRow(board, column) - 1;
}

export const puzzleFighterVisualColumnFor = (column: number, orientation: 0 | 1 | 2 | 3) => orientation === 3 ? column - 1 : column;

function alignPuzzleFighterFallingPair(pair: HTMLElement) {
  const board = pair.parentElement;
  const visualColumn = Number(pair.style.getPropertyValue("--pf-column"));
  if (!board || !Number.isInteger(visualColumn) || visualColumn < 0 || visualColumn >= PUZZLE_COLUMNS) return;
  const cells = board.querySelectorAll<HTMLElement>(":scope > .pf-gem");
  const firstCell = cells[visualColumn];
  const horizontal = pair.classList.contains("pf-falling-pair--1") || pair.classList.contains("pf-falling-pair--3");
  const lastCell = cells[visualColumn + (horizontal ? 1 : 0)];
  if (!firstCell || !lastCell) return;
  pair.style.left = `${firstCell.offsetLeft}px`;
  pair.style.width = `${lastCell.offsetLeft + lastCell.offsetWidth - firstCell.offsetLeft}px`;
}

export function puzzleFighterEngineColumnForAnchor(anchorColumn: number, orientation: 0 | 1 | 2 | 3) {
  if (orientation === 1) return Math.min(anchorColumn, PUZZLE_COLUMNS - 2);
  if (orientation === 3) return Math.max(anchorColumn, 1);
  return anchorColumn;
}

function fallingPairHtml(pair: PuzzlePair | null, board: Array<PuzzleGem | null>, playerId: string, pieceIndex: number, autoDropInMs: number | null | undefined, fallDurationMs: number | null | undefined, activeDrop?: { column: number; orientation: 0 | 1 | 2 | 3 } | null) {
  if (!pair || autoDropInMs === null || autoDropInMs === undefined || !activeDrop) return "";
  const duration = Math.max(250, Math.min(12_000, Math.round(fallDurationMs || autoDropInMs)));
  const remaining = Math.max(0, Math.min(duration, Math.round(autoDropInMs)));
  const visualColumn = puzzleFighterVisualColumnFor(activeDrop.column, activeDrop.orientation);
  const targetRow = puzzleFighterLandingRow(board, activeDrop.column, activeDrop.orientation);
  return `<span class="pf-falling-pair pf-falling-pair--${activeDrop.orientation}" style="--pf-column:${visualColumn}" data-pf-player="${escapeHtml(playerId)}" data-pf-piece="${pieceIndex}" data-pf-duration="${duration}" data-pf-remaining="${remaining}" data-pf-target-row="${targetRow}" aria-hidden="true">${gemHtml(pair.first)}${gemHtml(pair.second)}</span>`;
}

function boardHtml(state: PuzzlePlayerState, playerId: string, players: PuzzleFighterPlayer[], myId?: string, autoDropInMs?: number | null, fallDurationMs?: number | null, activeDrop?: { column: number; orientation: 0 | 1 | 2 | 3 } | null, sideIndex = 0) {
  const label = `${nameFor(players, playerId)}'s puzzle field, ${state.board.filter(Boolean).length} gems, ${state.pendingGarbage} incoming counter gems`;
  const fieldLabel = myId === undefined ? "FIGHTER FIELD" : playerId === myId ? "YOUR FIELD" : "RIVAL FIELD";
  const powerCells = visualPowerGemCells(state.board);
  const pressureClass = state.pendingGarbage >= 12 ? "is-danger" : state.pendingGarbage >= 6 ? "is-warning" : "";
  const isPlayerSide = myId === undefined ? sideIndex === 0 : playerId === myId;
  return `<article class="pf-field ${isPlayerSide ? "is-mine pf-field--player" : "pf-field--rival"} ${state.toppedOut ? "is-topped" : ""} ${pressureClass ? "has-pressure" : ""}" data-pf-player="${escapeHtml(playerId)}">
    <header><div><small>${fieldLabel}</small><strong>${escapeHtml(nameFor(players, playerId))}</strong></div><span><b>${state.score.toLocaleString()}</b> pts</span></header>
    <div class="pf-pressure ${pressureClass}"><span>INCOMING</span><b>${state.pendingGarbage}</b><i style="--pressure:${Math.min(100, state.pendingGarbage * 8)}%"></i></div>
    <div class="pf-board-shell"><span class="pf-danger-label" aria-hidden="true">DANGER · TOP OUT</span><div class="pf-board" role="img" aria-label="${escapeHtml(label)}">${state.board.map((gem, index) => gemHtml(gem, `${index < 6 ? "is-danger-row" : ""} ${visualPowerGemClasses(state.board, powerCells, index)}`.trim())).join("")}${fallingPairHtml(state.currentPair, state.board, playerId, state.pieceIndex, autoDropInMs, fallDurationMs, activeDrop)}</div>${state.toppedOut ? `<div class="pf-ko" aria-label="Knocked out"><b>KO</b><span>FIELD TOPPED OUT</span></div>` : ""}</div>
    <footer><span>Best chain <b>${state.maxChain || 0}×</b></span><span>Cleared <b>${state.cleared}</b></span></footer>
  </article>`;
}

function fighterStats(view: PuzzleFighterView, players: PuzzleFighterPlayer[]) {
  return `<div class="pf-versus">${view.playerOrder.map((id, index) => {
    const state = view.players[id];
    const player = players.find((candidate) => candidate.id === id);
    return `<div class="pf-versus__fighter pf-versus__fighter--${index ? "violet" : "cyan"}"><i aria-hidden="true">${index ? "◆" : "▲"}</i><span><strong>${escapeHtml(nameFor(players, id))}</strong><small>${!player?.connected ? "RECONNECTING" : state?.lastChain ? `${state.lastChain}× CHAIN · ${state.lastAttack} SENT` : "READY TO CRASH"}</small></span></div>`;
  }).join('<b class="pf-versus__mark">VS</b>')}</div>`;
}

export function puzzleFighterTileHtml(attributes: string, local = false) {
  return `<button class="game-tile game-tile--puzzle-fighter" ${attributes}><span class="game-tile__art pf-tile-art" aria-hidden="true"><i></i><i></i><i></i><i></i><b>✦</b></span><span class="game-tile__copy"><small>${local ? "LOCAL SOLO · AI RIVAL" : "2 PLAYERS · HEAD-TO-HEAD PUZZLE BATTLE"}</small><strong>Puzzle Fighter</strong><em>Build color chains, detonate crash gems, and bury your rival under counter-gems.</em></span><span class="game-tile__go" aria-hidden="true">→</span></button>`;
}

export function puzzleFighterLobbyHtml(view: PuzzleFighterView, players: PuzzleFighterPlayer[], myId: string, isAdmin: boolean) {
  const connected = players.filter((player) => player.connected);
  const canStart = isAdmin && connected.length === 2;
  return `<section class="pf-lobby"><header class="pf-lobby__hero"><div><p class="eyebrow">ARCADE-STYLE HEAD-TO-HEAD</p><h1>Puzzle <span>Fighter</span></h1><p>Both fighters build at once. Match colors, trigger them with crash gems, and send counter-gems across the versus screen.</p></div><div class="pf-logo" aria-hidden="true"><i></i><i></i><i></i><i></i><b>VS</b></div></header>
    <section class="pf-lobby__seats glass-card"><div class="section-title"><h2>Battle card</h2><span>${connected.length} / 2 READY</span></div><div>${connected.slice(0, 2).map((player, index) => `<article><i>${index ? "◆" : "▲"}</i><span><strong>${escapeHtml(player.name)}</strong><small>${player.id === myId ? "YOU" : player.isAdmin ? "ROOM ADMIN" : "CHALLENGER"}</small></span></article>`).join("")}${connected.length < 2 ? `<article class="is-empty"><i>?</i><span><strong>Waiting for a rival</strong><small>SHARE THE ROOM CODE</small></span></article>` : ""}</div></section>
    <section class="pf-rules"><article><b>1</b><strong>Stack pairs</strong><span>Choose a rotation and lane for every two-gem piece.</span></article><article><b>✦</b><strong>Trigger colors</strong><span>A crash gem clears its connected color group.</span></article><article><b>⚡</b><strong>Send pressure</strong><span>Big clears and chains rain counter-gems on your rival.</span></article></section>
    <div class="sticky-action"><button id="start-game" class="primary-button primary-button--large" ${canStart ? "" : "disabled"}><span>${canStart ? "Start the battle" : isAdmin ? "Waiting for exactly 2 fighters" : "Waiting for the room admin"}</span><b>⚡</b></button><button id="leave" class="text-button">Leave room</button></div></section>`;
}

let selectedOrientation: 0 | 1 | 2 | 3 = 0;
let selectedColumn = 2;
let selectedAnchorColumn = 2;
let selectedPlayerId = "";
let selectedPieceIndex = -1;

function placementFor(view: PuzzleFighterView, myId: string) {
  const state = view.players[myId];
  if (state && (selectedPlayerId !== myId || selectedPieceIndex !== state.pieceIndex)) {
    const active = view.activeDropsByPlayer?.[myId];
    selectedOrientation = active?.orientation ?? 0;
    selectedColumn = active?.column ?? 2;
    selectedAnchorColumn = puzzleFighterVisualColumnFor(selectedColumn, selectedOrientation);
    selectedPlayerId = myId;
    selectedPieceIndex = state.pieceIndex;
  }
  return { orientation: selectedOrientation, column: selectedColumn, anchorColumn: selectedAnchorColumn };
}

function controlsHtml(view: PuzzleFighterView, myId: string) {
  const state = view.players[myId];
  if (!state) return `<p class="pf-spectator">You’re watching this head-to-head battle.</p>`;
  const speed = view.fallDurationMs === undefined ? "" : `<label class="pf-speed"><span><b>Speed</b><output data-pf-speed-output>${(view.fallDurationMs / 1000).toFixed(1)}s</output></span><input type="range" min="1000" max="10000" step="500" value="${view.fallDurationMs}" data-pf-speed aria-label="Puzzle Fighter fall speed, fast to slow"></label>`;
  return `<section class="pf-controls" aria-label="Piece preview and speed"><div class="pf-queue">${pairHtml(state.currentPair, "IN PLAY")}${pairHtml(state.nextPair, "NEXT")}</div>${speed}<div class="pf-control-info"><span class="pf-key-hint"><kbd>←</kbd><kbd>→</kbd> move <kbd>↑</kbd> rotate <kbd>↓</kbd> drop</span><p data-pf-status>Ready</p></div></section>`;
}

function touchControlsHtml(view: PuzzleFighterView, myId: string) {
  if (!view.players[myId]) return "";
  return `<nav class="pf-touch" aria-label="Touch game controls"><div class="pf-touch__directions"><button type="button" data-pf-move="left" aria-label="Move left">◀</button><button type="button" data-pf-move="right" aria-label="Move right">▶</button></div><button type="button" data-pf-rotate aria-label="Rotate gems"><b aria-hidden="true">↻</b><span>Rotate</span></button><button type="button" data-pf-hard-drop aria-label="Hard drop gems"><b aria-hidden="true">↓</b><span>Drop</span></button></nav>`;
}

export function puzzleFighterGameHtml(view: PuzzleFighterView, players: PuzzleFighterPlayer[], myId: string) {
  return `<section class="pf-game"><header class="pf-game__head"><h1>Puzzle Fighter</h1><b>${view.moveCount} DROPS</b></header>${fighterStats(view, players)}${controlsHtml(view, myId)}<div class="pf-fields">${view.playerOrder.map((id) => view.players[id] ? boardHtml(view.players[id]!, id, players, myId, view.autoDropInMsByPlayer?.[id], view.fallDurationMsByPlayer?.[id], view.activeDropsByPlayer?.[id]) : "").join("")}</div>${touchControlsHtml(view, myId)}</section>`;
}

export function puzzleFighterDisplayHtml(view: PuzzleFighterView, players: PuzzleFighterPlayer[]) {
  return `<section class="pf-game pf-game--display"><header class="pf-game__head"><div><p>PUZZLE FIGHTER · LIVE BATTLE</p><h1>Head-to-head <span>gem combat</span></h1></div><b>${view.moveCount} DROPS</b></header>${fighterStats(view, players)}<div class="pf-fields">${view.playerOrder.map((id, index) => view.players[id] ? boardHtml(view.players[id]!, id, players, undefined, view.autoDropInMsByPlayer?.[id], view.fallDurationMsByPlayer?.[id], view.activeDropsByPlayer?.[id], index) : "").join("")}</div></section>`;
}

export function puzzleFighterFinalHtml(view: PuzzleFighterView, players: PuzzleFighterPlayer[]) {
  const winner = view.winnerId ? nameFor(players, view.winnerId) : "A fighter";
  return `<section class="pf-final"><header><small>KO · FIELD TOP-OUT</small><h2>${escapeHtml(winner)} wins the clash!</h2><p>Chains, clears, and every counter-gem are locked into the final boards.</p></header>${fighterStats(view, players)}<div class="pf-fields">${view.playerOrder.map((id, index) => view.players[id] ? boardHtml(view.players[id]!, id, players, undefined, undefined, undefined, undefined, index) : "").join("")}</div></section>`;
}

const fallingVisualProgress = new Map<string, number>();
let lastAnimatedMoveCount = -1;
const puzzleFighterBindingCleanups = new WeakMap<HTMLElement, () => void>();
export type PuzzleFighterControlAction = "move_left" | "move_right" | "rotate" | "hard_drop";
export type PuzzleFighterGamepadLike = {
  id?: string;
  index?: number;
  axes: ArrayLike<number>;
  buttons: ArrayLike<{ pressed?: boolean; value?: number }>;
};
const puzzleFighterGamepadState: {
  key: string;
  pressed: Set<PuzzleFighterControlAction>;
  horizontalRepeatAt: number;
} = { key: "", pressed: new Set(), horizontalRepeatAt: 0 };

export function unbindPuzzleFighter(root: HTMLElement) {
  puzzleFighterBindingCleanups.get(root)?.();
  puzzleFighterBindingCleanups.delete(root);
}

export function puzzleFighterKeyboardAction(key: string, repeat = false): PuzzleFighterControlAction | null {
  if (repeat && ["ArrowDown", " "].includes(key)) return null;
  if (key === "ArrowLeft") return "move_left";
  if (key === "ArrowRight") return "move_right";
  if (["ArrowUp", "x", "X"].includes(key)) return "rotate";
  if (["ArrowDown", " "].includes(key)) return "hard_drop";
  return null;
}

export function puzzleFighterGamepadActions(gamepad: PuzzleFighterGamepadLike | null | undefined): PuzzleFighterControlAction[] {
  if (!gamepad) return [];
  const pressed = (index: number) => {
    const button = gamepad.buttons[index];
    return Boolean(button?.pressed || (button?.value || 0) >= .5);
  };
  const horizontal = Number(gamepad.axes[0]) || 0;
  const vertical = Number(gamepad.axes[1]) || 0;
  const horizontalIsDominant = Math.abs(horizontal) >= Math.abs(vertical);
  const verticalIsDominant = Math.abs(vertical) > Math.abs(horizontal);
  const actions: PuzzleFighterControlAction[] = [];
  if (pressed(14) || (horizontalIsDominant && horizontal <= -.55)) actions.push("move_left");
  else if (pressed(15) || (horizontalIsDominant && horizontal >= .55)) actions.push("move_right");
  if (pressed(12) || pressed(0) || pressed(2)) actions.push("rotate");
  if (pressed(13) || pressed(1) || pressed(3) || (verticalIsDominant && vertical >= .7)) actions.push("hard_drop");
  return actions;
}

export function animatePuzzleFighterDrops(root: HTMLElement) {
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  root.querySelectorAll<HTMLElement>(".pf-falling-pair").forEach((pair) => {
    const board = pair.parentElement;
    const duration = Number(pair.dataset.pfDuration);
    const remaining = Number(pair.dataset.pfRemaining);
    if (!board || !Number.isFinite(duration) || duration <= 0 || !Number.isFinite(remaining)) return;
    const playerId = pair.dataset.pfPlayer || "fighter";
    const visualKey = `${playerId}:${pair.dataset.pfPiece || "0"}`;
    for (const key of fallingVisualProgress.keys()) if (key.startsWith(`${playerId}:`) && key !== visualKey) fallingVisualProgress.delete(key);
    const startedAt = performance.now() - Math.max(0, duration - remaining);
    const initialProgress = Math.max(0, Math.min(1, (duration - remaining) / duration));
    const rememberedProgress = fallingVisualProgress.get(visualKey);
    if (rememberedProgress !== undefined && rememberedProgress - initialProgress > 0.45) fallingVisualProgress.delete(visualKey);
    let measuredClass = "";
    let travel = 0;
    let travelFrom = 0;
    let travelTarget = 0;
    let travelChangedAt = 0;
    const draw = (now: number) => {
      if (!pair.isConnected) return;
      const targetRow = Number(pair.dataset.pfTargetRow);
      const measurementKey = `${pair.className}:${pair.style.getPropertyValue("--pf-column")}:${targetRow}:${board.clientWidth}`;
      if (measuredClass !== measurementKey) {
        measuredClass = measurementKey;
        alignPuzzleFighterFallingPair(pair);
        const cells = board.querySelectorAll<HTMLElement>(":scope > .pf-gem");
        const targetCell = Number.isInteger(targetRow) && targetRow >= 0 ? cells[targetRow * PUZZLE_COLUMNS] : null;
        const measuredTravel = Math.max(0, targetCell ? targetCell.offsetTop - pair.offsetTop : 0);
        travelFrom = travel || measuredTravel;
        travelTarget = measuredTravel;
        travelChangedAt = now;
      }
      const travelBlend = Math.max(0, Math.min(1, (now - travelChangedAt) / 150));
      const easedBlend = 1 - Math.pow(1 - travelBlend, 3);
      travel = travelFrom + (travelTarget - travelFrom) * easedBlend;
      const authoritativeProgress = Math.max(0, Math.min(1, (now - startedAt) / duration));
      const progress = Math.max(authoritativeProgress, fallingVisualProgress.get(visualKey) || 0);
      fallingVisualProgress.set(visualKey, progress);
      pair.style.transform = `translate3d(0,${(travel * progress).toFixed(2)}px,0)`;
      if (!reduceMotion && progress < 1) window.requestAnimationFrame(draw);
    };
    draw(performance.now());
  });
}

export function animatePuzzleFighterImpact(root: HTMLElement, view: PuzzleFighterView) {
  if (!view.lastMove || view.moveCount === lastAnimatedMoveCount) return;
  lastAnimatedMoveCount = view.moveCount;
  const field = [...root.querySelectorAll<HTMLElement>(".pf-field")].find((candidate) => candidate.dataset.pfPlayer === view.lastMove?.playerId);
  if (!field) return;
  field.classList.add("is-landing");
  if (view.lastMove.chain > 0 || view.lastMove.attack > 0) field.classList.add("is-chain-impact");
  window.setTimeout(() => field.classList.remove("is-landing", "is-chain-impact"), 520);
}

export function bindPuzzleFighter(root: HTMLElement, view: PuzzleFighterView, myId: string, send: (message: Record<string, unknown>) => void) {
  unbindPuzzleFighter(root);
  animatePuzzleFighterDrops(root);
  animatePuzzleFighterImpact(root, view);
  const myState = view.players[myId];
  if (!myState) return;
  let bindingActive = true;
  let readyTimer: number | undefined;
  let countdownTicker: number | undefined;
  let gamepadFrame: number | undefined;
  let { orientation, column, anchorColumn } = placementFor(view, myId);
  const localFallingMode = view.activeDropsByPlayer !== undefined;
  const orientationButtons = [...root.querySelectorAll<HTMLButtonElement>("[data-pf-orientation]")];
  const columnButtons = [...root.querySelectorAll<HTMLButtonElement>("[data-pf-column]")];
  const moveButtons = [...root.querySelectorAll<HTMLButtonElement>("[data-pf-move]")];
  const rotateButton = root.querySelector<HTMLButtonElement>("[data-pf-rotate]");
  const hardDropButton = root.querySelector<HTMLButtonElement>("[data-pf-hard-drop]");
  const speedSlider = root.querySelector<HTMLInputElement>("[data-pf-speed]");
  const speedOutput = root.querySelector<HTMLOutputElement>("[data-pf-speed-output]");
  const fallingPair = root.querySelector<HTMLElement>(".pf-field.is-mine .pf-falling-pair");
  const status = root.querySelector<HTMLElement>("[data-pf-status]");
  const clientReadyAt = view.canAct ? 0 : view.canActInMs === null ? Number.POSITIVE_INFINITY : Date.now() + view.canActInMs;
  const autoDropInMs = view.autoDropInMsByPlayer?.[myId];
  const autoDropAt = autoDropInMs === null || autoDropInMs === undefined ? Number.POSITIVE_INFINITY : Date.now() + autoDropInMs;
  const engineColumnFor = (candidateAnchor: number, candidateOrientation: 0 | 1 | 2 | 3) => puzzleFighterEngineColumnForAnchor(candidateAnchor, candidateOrientation);
  const isLegal = (candidateAnchor: number, candidateOrientation: 0 | 1 | 2 | 3) => {
    const candidateColumn = engineColumnFor(candidateAnchor, candidateOrientation);
    return view.legalDrops.some((drop) => drop.orientation === candidateOrientation && drop.column === candidateColumn);
  };
  const targetTravelFor = (candidateColumn: number, candidateOrientation: 0 | 1 | 2 | 3) => {
    if (!fallingPair?.parentElement) return Number.POSITIVE_INFINITY;
    const targetRow = puzzleFighterLandingRow(myState.board, candidateColumn, candidateOrientation);
    const targetCell = fallingPair.parentElement.querySelectorAll<HTMLElement>(":scope > .pf-gem")[targetRow * PUZZLE_COLUMNS];
    return targetCell ? Math.max(0, targetCell.offsetTop - fallingPair.offsetTop) : -1;
  };
  const currentTravel = () => {
    const match = /translate3d\(0(?:px)?,\s*([-\d.]+)px/i.exec(fallingPair?.style.transform || "");
    return match ? Number(match[1]) : 0;
  };
  const hasRoomAtCurrentHeight = (candidateColumn: number, candidateOrientation: 0 | 1 | 2 | 3) => currentTravel() <= targetTravelFor(candidateColumn, candidateOrientation) + 1;
  const refresh = () => {
    if (!bindingActive || !root.isConnected) return;
    const ready = view.canAct || Date.now() >= clientReadyAt;
    orientationButtons.forEach((button) => { const selected = Number(button.dataset.pfOrientation) === orientation; button.classList.toggle("is-selected", selected); button.setAttribute("aria-pressed", String(selected)); });
    columnButtons.forEach((button) => {
      const candidate = Number(button.dataset.pfColumn);
      const candidateColumn = engineColumnFor(candidate, orientation);
      button.disabled = (!localFallingMode && !ready) || !isLegal(candidate, orientation) || !hasRoomAtCurrentHeight(candidateColumn, orientation);
      button.classList.toggle("is-selected", candidate === anchorColumn);
    });
    if (hardDropButton) hardDropButton.disabled = !ready || !view.legalDrops.some((drop) => drop.orientation === orientation && drop.column === column);
    if (fallingPair) {
      const nextOrientationClass = `pf-falling-pair--${orientation}`;
      const orientationChanged = !fallingPair.classList.contains(nextOrientationClass);
      fallingPair.classList.remove("pf-falling-pair--0", "pf-falling-pair--1", "pf-falling-pair--2", "pf-falling-pair--3");
      fallingPair.classList.add(nextOrientationClass);
      fallingPair.style.setProperty("--pf-column", String(puzzleFighterVisualColumnFor(column, orientation)));
      alignPuzzleFighterFallingPair(fallingPair);
      fallingPair.dataset.pfTargetRow = String(puzzleFighterLandingRow(myState.board, column, orientation));
      if (orientationChanged) {
        fallingPair.classList.remove("is-turning");
        window.requestAnimationFrame(() => fallingPair.classList.add("is-turning"));
        window.setTimeout(() => fallingPair.classList.remove("is-turning"), 190);
      }
    }
    if (status && Number.isFinite(autoDropAt)) {
      const remaining = Math.max(0, autoDropAt - Date.now());
      status.textContent = `Locks in ${(remaining / 1000).toFixed(1)}s`;
    } else if (status && ready) status.textContent = "Ready to drop";
  };
  const setPlacement = (nextAnchorColumn: number, nextOrientation: 0 | 1 | 2 | 3) => {
    const nextColumn = engineColumnFor(nextAnchorColumn, nextOrientation);
    if (!isLegal(nextAnchorColumn, nextOrientation) || !hasRoomAtCurrentHeight(nextColumn, nextOrientation)) return false;
    column = nextColumn;
    anchorColumn = nextAnchorColumn;
    orientation = nextOrientation;
    selectedColumn = column;
    selectedAnchorColumn = anchorColumn;
    selectedOrientation = orientation;
    if (localFallingMode) send({ type: "puzzle_fighter_steer", column, orientation, expectedPieceIndex: myState.pieceIndex });
    refresh();
    return true;
  };
  const move = (direction: -1 | 1) => {
    for (let candidate = anchorColumn + direction; candidate >= 0 && candidate < PUZZLE_COLUMNS; candidate += direction) if (setPlacement(candidate, orientation)) return;
  };
  const rotate = () => {
    for (let offset = 1; offset <= 4; offset += 1) {
      const candidate = ((orientation + offset) % 4) as 0 | 1 | 2 | 3;
      if (setPlacement(anchorColumn, candidate)) return;
    }
  };
  const hardDrop = () => {
    const ready = view.canAct || Date.now() >= clientReadyAt;
    if (!ready || !view.legalDrops.some((drop) => drop.orientation === orientation && drop.column === column)) return;
    columnButtons.forEach((candidate) => { candidate.disabled = true; });
    if (hardDropButton) hardDropButton.disabled = true;
    fallingPair?.classList.add("is-hard-dropping");
    send({ type: "puzzle_fighter_drop", column, orientation, expectedPieceIndex: myState.pieceIndex });
    window.setTimeout(refresh, 180);
  };
  orientationButtons.forEach((button) => button.addEventListener("click", () => setPlacement(anchorColumn, Number(button.dataset.pfOrientation) as 0 | 1 | 2 | 3)));
  columnButtons.forEach((button) => button.addEventListener("click", () => {
    setPlacement(Number(button.dataset.pfColumn), orientation);
    if (!localFallingMode) hardDrop();
  }));
  moveButtons.forEach((button) => button.addEventListener("click", () => move(button.dataset.pfMove === "left" ? -1 : 1)));
  rotateButton?.addEventListener("click", rotate);
  hardDropButton?.addEventListener("click", hardDrop);
  speedSlider?.addEventListener("input", () => { if (speedOutput) speedOutput.value = `${(Number(speedSlider.value) / 1000).toFixed(1)}s`; });
  speedSlider?.addEventListener("change", () => send({ type: "puzzle_fighter_speed", fallDurationMs: Number(speedSlider.value) }));
  root.tabIndex = 0;
  const handleKeydown = (event: KeyboardEvent) => {
    const action = puzzleFighterKeyboardAction(event.key, event.repeat);
    if (!action) return;
    if (event.target instanceof HTMLInputElement) return;
    if (event.key === " " && event.target instanceof HTMLButtonElement) return;
    event.preventDefault();
    if (action === "move_left") move(-1);
    else if (action === "move_right") move(1);
    else if (action === "rotate") rotate();
    else hardDrop();
  };
  const runControlAction = (action: PuzzleFighterControlAction) => {
    if (action === "move_left") move(-1);
    else if (action === "move_right") move(1);
    else if (action === "rotate") rotate();
    else hardDrop();
  };
  const pollGamepads = (now: number) => {
    if (!bindingActive || !root.isConnected) return;
    const gamepad = Array.from(navigator.getGamepads?.() || []).find((candidate): candidate is Gamepad => Boolean(candidate));
    if (!gamepad) {
      puzzleFighterGamepadState.key = "";
      puzzleFighterGamepadState.pressed.clear();
      puzzleFighterGamepadState.horizontalRepeatAt = 0;
    } else {
      const key = `${gamepad.index}:${gamepad.id}`;
      const actions = puzzleFighterGamepadActions(gamepad);
      const pressed = new Set(actions);
      if (key !== puzzleFighterGamepadState.key) {
        puzzleFighterGamepadState.key = key;
        puzzleFighterGamepadState.pressed = pressed;
        puzzleFighterGamepadState.horizontalRepeatAt = 0;
      } else {
        const toRun: PuzzleFighterControlAction[] = [];
        const horizontal = actions.find((action) => action === "move_left" || action === "move_right");
        if (horizontal) {
          if (!puzzleFighterGamepadState.pressed.has(horizontal)) {
            toRun.push(horizontal);
            puzzleFighterGamepadState.horizontalRepeatAt = now + 320;
          } else if (now >= puzzleFighterGamepadState.horizontalRepeatAt) {
            toRun.push(horizontal);
            puzzleFighterGamepadState.horizontalRepeatAt = now + 140;
          }
        } else {
          puzzleFighterGamepadState.horizontalRepeatAt = 0;
        }
        for (const action of actions) {
          if ((action === "rotate" || action === "hard_drop") && !puzzleFighterGamepadState.pressed.has(action)) toRun.push(action);
        }
        puzzleFighterGamepadState.pressed = pressed;
        toRun.forEach(runControlAction);
      }
    }
    gamepadFrame = window.requestAnimationFrame(pollGamepads);
  };
  root.addEventListener("keydown", handleKeydown);
  root.focus({ preventScroll: true });
  if (typeof navigator.getGamepads === "function") gamepadFrame = window.requestAnimationFrame(pollGamepads);
  refresh();
  if (!view.canAct && Number.isFinite(clientReadyAt)) readyTimer = window.setTimeout(refresh, Math.max(0, clientReadyAt - Date.now()) + 5);
  if (Number.isFinite(autoDropAt)) {
    countdownTicker = window.setInterval(() => {
      if (!bindingActive || !root.isConnected || Date.now() >= autoDropAt) window.clearInterval(countdownTicker);
      else refresh();
    }, 100);
  }
  puzzleFighterBindingCleanups.set(root, () => {
    bindingActive = false;
    root.removeEventListener("keydown", handleKeydown);
    if (readyTimer !== undefined) window.clearTimeout(readyTimer);
    if (countdownTicker !== undefined) window.clearInterval(countdownTicker);
    if (gamepadFrame !== undefined) window.cancelAnimationFrame(gamepadFrame);
  });
}
